import { useEffect, useMemo, useRef, useState } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import type { BackendHealthIndicatorProps } from './BackendHealthIndicator';
import { presentBackendHealthError } from './backendHealthMessages';
import {
  parseReadinessResponse,
  probeBackendUsable,
  type ParsedReadiness,
} from './backendReadinessCheck';

export type { BackendHealthIndicatorProps };

/** While `./startDev` / `yarn start` bring the API up, readiness often errors briefly—avoid calling that a hard failure until this elapses (or we have seen `ok` once). */
const STARTUP_GRACE_MS = 120_000;

function startingUpCheckingProps(detail?: string): BackendHealthIndicatorProps {
  const baseMessage =
    'The development backend is still starting up. This panel will clear when the API is ready; checks run every few seconds.';

  return {
    panelVariant: 'checking',
    showPollProgress: false,
    panelTitle: 'Backend starting up',
    panelError: new Error(
      detail ? [baseMessage, '', detail].join('\n') : baseMessage,
    ),
  };
}

type ReadinessOutcome =
  /** No response yet (initial visit, including login page — before first fetch completes). */
  | { kind: 'pending' }
  | ParsedReadiness;

function rawDetailFromOutcome(outcome: ReadinessOutcome): string {
  if (outcome.kind === 'failed') {
    return outcome.error.message;
  }
  if (outcome.kind === 'unhealthy') {
    return typeof outcome.health.message === 'string' && outcome.health.message
      ? outcome.health.message
      : `Readiness status: ${outcome.health.status}`;
  }
  return '';
}

function fallbackTitleForOutcome(outcome: ReadinessOutcome): string {
  return outcome.kind === 'failed' ? 'Backend failed or is down' : 'Backend not ready';
}

/**
 * Fetches readiness in local dev and returns panel props when the panel should show.
 * Returns null when hidden (non-dev, healthy, etc.).
 */
export function useBackendHealthIndicatorData(): BackendHealthIndicatorProps | null {
  const configApi = useApi(configApiRef);
  const [outcome, setOutcome] = useState<ReadinessOutcome>({ kind: 'pending' });
  const [backendUsable, setBackendUsable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setGraceElapsed] = useState(0);
  const mountTimeRef = useRef(Date.now());
  const hasBeenHealthyRef = useRef(false);

  const backendBaseUrl = useMemo(
    () => configApi.getString('backend.baseUrl').replace(/\/$/, ''),
    [configApi],
  );

  const readinessUrl = useMemo(
    () => `${backendBaseUrl}/.backstage/health/v1/readiness`,
    [backendBaseUrl],
  );

  const isDevelopment =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  useEffect(() => {
    if (!isDevelopment) return;
    const graceTimer = window.setTimeout(() => {
      setGraceElapsed(n => n + 1);
    }, STARTUP_GRACE_MS);
    return () => clearTimeout(graceTimer);
  }, [isDevelopment]);

  useEffect(() => {
    if (!isDevelopment) return;

    const checkHealth = async () => {
      setLoading(true);
      try {
        const [readinessResponse, usable] = await Promise.all([
          fetch(readinessUrl, { method: 'GET' }),
          probeBackendUsable(backendBaseUrl),
        ]);
        setBackendUsable(usable);

        const parsed = await parseReadinessResponse(readinessResponse);
        if (parsed.kind === 'healthy') {
          hasBeenHealthyRef.current = true;
        }
        setOutcome(parsed);
      } catch (err) {
        setBackendUsable(false);
        setOutcome({
          kind: 'failed',
          error: err instanceof Error ? err : new Error('Unknown error'),
        });
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 5000);
    return () => clearInterval(interval);
  }, [isDevelopment, readinessUrl, backendBaseUrl]);

  if (!isDevelopment) {
    return null;
  }

  if (outcome.kind === 'healthy') {
    return null;
  }

  // Readiness stays 503 while plugins init, but liveness + guest auth may already work.
  if (outcome.kind === 'starting' && backendUsable) {
    return null;
  }

  if (outcome.kind === 'pending' || outcome.kind === 'starting') {
    const detail =
      outcome.kind === 'starting' && outcome.message
        ? `Latest check: ${outcome.message}`
        : undefined;

    if (
      outcome.kind === 'starting' &&
      !hasBeenHealthyRef.current &&
      Date.now() - mountTimeRef.current >= STARTUP_GRACE_MS
    ) {
      return startingUpCheckingProps(
        [
          detail,
          'Startup is taking longer than usual. If the app already works, this panel should disappear once sign-in succeeds. Otherwise check ./rundev.sh logs (often a missing DB tunnel on localhost:5432).',
        ]
          .filter(Boolean)
          .join('\n\n'),
      );
    }

    return startingUpCheckingProps(detail);
  }

  const rawDetail = rawDetailFromOutcome(outcome);
  const fallbackTitle = fallbackTitleForOutcome(outcome);
  const presented = presentBackendHealthError(rawDetail, fallbackTitle);

  const withinStartupGrace =
    !hasBeenHealthyRef.current &&
    Date.now() - mountTimeRef.current < STARTUP_GRACE_MS;

  if (withinStartupGrace && (outcome.kind === 'failed' || outcome.kind === 'unhealthy')) {
    const base = startingUpCheckingProps();
    return {
      ...base,
      panelError: new Error(
        [base.panelError.message, '', `Latest check: ${presented.message}`].join('\n'),
      ),
    };
  }

  const showPollProgress = outcome.kind === 'unhealthy' && loading;

  return {
    showPollProgress,
    panelTitle: presented.title,
    panelError: new Error(presented.message),
  };
}
